import React, { useState, useRef, useEffect } from "react";
import { Box, Button, Typography, IconButton, Stack } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import women from "../../images/woman.png";
import {
  Send as SendIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  KeyboardArrowDown as CollapseIcon,
} from "@mui/icons-material";

const getWsUrl = () => {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws`;
};

// ── Keyframes ──────────────────────────────────────────────────────────────
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40%           { transform: translateY(-5px); opacity: 1; }
`;

const fabPop = keyframes`
  0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
  65%  { transform: scale(1.1) rotate(3deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const ripple = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(25,118,210,0.3); }
  100% { box-shadow: 0 0 0 12px rgba(25,118,210,0); }
`;

// ── Styled Components ──────────────────────────────────────────────────────
const ChatWindow = styled(Box)(() => ({
  fontFamily: "'Host Grotesk', sans-serif",
  position: "fixed",
  bottom: 24,
  right: 24,
  width: 390,
  maxWidth: "calc(100vw - 32px)",
  height: 620,
  maxHeight: "calc(100dvh - 48px)",
  display: "flex",
  flexDirection: "column",
  borderRadius: 20,
  overflow: "hidden",
  background: "#ffffff",
  boxShadow:
    "0 4px 6px rgba(0,0,0,0.04), 0 12px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)",
  zIndex: 9999,
  animation: `${slideUp} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both`,
  "@media (max-width: 480px)": {
    bottom: 0,
    right: 0,
    left: 0,
    width: "100%",
    maxWidth: "100%",
    height: "88dvh",
    borderRadius: "18px 18px 0 0",
  },
}));

const Header = styled(Box)(() => ({
  flexShrink: 0,
  padding: "12px 14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
}));

const AvatarWrap = styled(Box)(() => ({
  position: "relative",
  width: 42,
  height: 42,
  borderRadius: "50%",
  flexShrink: 0,
  "& .ring": {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    overflow: "hidden",
    border: "2.5px solid rgba(255,255,255,0.35)",
    "& img": { width: "100%", height: "100%", objectFit: "cover" },
  },
}));

const OnlineDot = styled(Box)(({ connected }) => ({
  position: "absolute",
  bottom: 1,
  right: 1,
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: connected ? "#4ade80" : "#f87171",
  border: "2px solid #1976d2",
  transition: "background .3s",
}));

const HeaderBtn = styled(IconButton)(() => ({
  color: "rgba(255,255,255,0.7)",
  width: 30,
  height: 30,
  transition: "all .18s",
  "&:hover": {
    color: "#fff",
    background: "rgba(255,255,255,0.15)",
  },
}));

const MessagesArea = styled(Box)(() => ({
  flex: "1 1 auto",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  padding: "18px 14px 8px",
  gap: 10,
  minHeight: 0,
  background: "#f8fafc",
  "& > *": { flex: "0 0 auto" },
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "#cbd5e1",
    borderRadius: 4,
  },
  "&::-webkit-scrollbar-thumb:hover": { background: "#94a3b8" },
}));

const MsgRow = styled(Box)(({ isbot }) => ({
  display: "flex",
  justifyContent: isbot === "true" ? "flex-start" : "flex-end",
  animation: `${fadeIn} 0.22s ease both`,
}));

const Bubble = styled(Box)(({ isbot }) => ({
  maxWidth: "78%",
  padding: "10px 14px",
  borderRadius: isbot === "true" ? "4px 16px 16px 16px" : "16px 4px 16px 16px",
  background:
    isbot === "true" ? "#ffffff" : "linear-gradient(135deg, #1976d2, #1565c0)",
  color: isbot === "true" ? "#1e293b" : "#ffffff",
  fontSize: "0.895rem",
  lineHeight: 1.55,
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",
  boxShadow:
    isbot === "true"
      ? "0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.05)"
      : "0 4px 14px rgba(21,101,192,0.28)",
  transition: "transform .15s",
  "&:hover": { transform: "translateY(-1px)" },
}));

const Timestamp = styled(Typography)(() => ({
  fontSize: "0.66rem",
  color: "#94a3b8",
  marginTop: 3,
  letterSpacing: "0.01em",
}));

const PresetGrid = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 5,
  marginTop: 8,
}));

const PresetBtn = styled(Button)(() => ({
  justifyContent: "flex-start",
  padding: "8px 12px",
  borderRadius: 10,
  background: "#f0f7ff",
  color: "#1976d2",
  border: "1px solid #bfdbfe",
  textTransform: "none",
  fontSize: "0.82rem",
  lineHeight: 1.4,
  textAlign: "left",
  fontFamily: "'Host Grotesk', sans-serif",
  fontWeight: 500,
  gap: 8,
  transition: "all .18s",
  "&:hover": {
    background: "#dbeafe",
    borderColor: "#93c5fd",
    transform: "translateX(3px)",
    boxShadow: "none",
  },
  "&.Mui-disabled": { opacity: 0.4 },
}));

const TypingDot = styled(Box)(({ delay }) => ({
  width: 7,
  height: 7,
  borderRadius: "50%",
  background: "#94a3b8",
  animation: `${dotBounce} 1.2s ease infinite`,
  animationDelay: delay,
}));

const TypingBubble = () => (
  <MsgRow isbot="true">
    <Bubble
      isbot="true"
      sx={{ display: "flex", gap: "5px", alignItems: "center", py: "12px" }}
    >
      <TypingDot delay="0s" />
      <TypingDot delay="0.2s" />
      <TypingDot delay="0.4s" />
    </Bubble>
  </MsgRow>
);

const InputArea = styled(Box)(() => ({
  flexShrink: 0,
  padding: "10px 12px 12px",
  background: "#ffffff",
  borderTop: "1px solid #e9eef4",
}));

const InputRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "flex-end",
  gap: 8,
  background: "#f1f5f9",
  borderRadius: 14,
  padding: "8px 8px 8px 14px",
  border: "1.5px solid #e2e8f0",
  transition: "border-color .2s, box-shadow .2s, background .2s",
  "&:focus-within": {
    borderColor: "#93c5fd",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(59,130,246,0.1)",
  },
}));

const ChatTextarea = styled("textarea")(() => ({
  flex: 1,
  background: "transparent",
  border: "none",
  outline: "none",
  resize: "none",
  color: "#1e293b",
  fontFamily: "'Host Grotesk', sans-serif",
  fontSize: "0.9rem",
  lineHeight: 1.5,
  maxHeight: 120,
  overflowY: "auto",
  "&::placeholder": { color: "#94a3b8" },
  "&::-webkit-scrollbar": { width: 3 },
  "&::-webkit-scrollbar-thumb": { background: "#cbd5e1", borderRadius: 3 },
}));

const SendButton = styled(IconButton)(({ cansend }) => ({
  width: 34,
  height: 34,
  borderRadius: 9,
  flexShrink: 0,
  background:
    cansend === "true"
      ? "linear-gradient(135deg, #1976d2, #1565c0)"
      : "#e2e8f0",
  color: cansend === "true" ? "#fff" : "#94a3b8",
  transition: "all .2s",
  "&:hover": {
    background:
      cansend === "true"
        ? "linear-gradient(135deg, #2196f3, #1976d2)"
        : "#e2e8f0",
    transform: cansend === "true" ? "scale(1.06)" : "none",
  },
}));

const FabBtn = styled(Box)(() => ({
  position: "fixed",
  bottom: 24,
  right: 24,
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: "linear-gradient(135deg, #1976d2, #1565c0)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  zIndex: 9999,
  animation: `${fabPop} 0.4s cubic-bezier(0.34,1.56,0.64,1) both`,
  boxShadow: "0 6px 24px rgba(21,101,192,0.45)",
  transition: "transform .2s, box-shadow .2s",
  "&:hover": {
    transform: "scale(1.08)",
    boxShadow: "0 10px 30px rgba(21,101,192,0.55)",
    animation: `${ripple} 1s ease-out infinite`,
  },
  "@media (max-width: 480px)": { bottom: 20, right: 20 },
}));

// ── Helpers ────────────────────────────────────────────────────────────────
const formatTime = (d) =>
  d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
};

const MAX_CHARS = 500;

// ── Component ──────────────────────────────────────────────────────────────
const ChatInterface = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! How can I help you today? Here are some questions you might find helpful:",
      isBot: true,
      showPresetQuestions: true,
      time: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [shouldCloseWebSocket, setShouldCloseWebSocket] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const presetQuestions = [
    "How do I know the status of my fetching sessions?",
    "How do I generate and download reports?",
    "How does the date range filter affect dashboard visualizations?",
    "What are the IT section 31B Violations Monitored?",
    "How do I fetch new data?",
  ];

  const ws = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeout = useRef(null);

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
      setTimeout(() => inputRef.current?.focus(), 320);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setShouldCloseWebSocket(false);
      const connect = () => {
        ws.current = new WebSocket(`${getWsUrl()}/chat`);

        ws.current.onopen = () => setIsConnected(true);

        ws.current.onmessage = (event) => {
          clearTimeout(typingTimeout.current);
          setIsTyping(false);
          try {
            const res = JSON.parse(event.data);
            const text = res.message || res;
            setMessages((prev) => [
              ...prev,
              { id: prev.length + 1, text, isBot: true, time: new Date() },
            ]);
            if (!isOpen) setUnreadCount((c) => c + 1);
          } catch {
            setMessages((prev) => [
              ...prev,
              {
                id: prev.length + 1,
                text: event.data,
                isBot: true,
                time: new Date(),
              },
            ]);
          }
        };

        ws.current.onerror = () => setIsConnected(false);

        ws.current.onclose = () => {
          setIsConnected(false);
          setIsTyping(false);
          if (!shouldCloseWebSocket) setTimeout(connect, 3000);
        };
      };
      connect();
    } else {
      setShouldCloseWebSocket(true);
      if (ws.current) {
        ws.current.onclose = () => setIsConnected(false);
        ws.current.close();
      }
    }
    return () => {
      if (ws.current) {
        setShouldCloseWebSocket(true);
        ws.current.close();
      }
    };
  }, [isOpen]);

  const sendMessage = (text) => {
    if (!text.trim() || !ws.current || ws.current.readyState !== WebSocket.OPEN)
      return;
    try {
      ws.current.send(text);
      setMessages((prev) => [
        ...prev,
        { id: prev.length + 1, text, isBot: false, time: new Date() },
      ]);
      setIsTyping(true);
      clearTimeout(typingTimeout.current);
      typingTimeout.current = setTimeout(() => setIsTyping(false), 10000);
    } catch (err) {
      console.error("Send error:", err);
    }
  };

  const handleSend = () => {
    if (!inputText.trim() || inputText.length > MAX_CHARS) return;
    sendMessage(inputText.trim());
    setInputText("");
    if (inputRef.current) inputRef.current.style.height = "auto";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    setShouldCloseWebSocket(true);
    setIsOpen(false);
    if (ws.current) ws.current.close();
  };

  const canSend =
    inputText.trim().length > 0 && isConnected && inputText.length <= MAX_CHARS;

  // ── FAB ──────────────────────────────────────────────────────────────
  if (!isOpen) {
    return (
      <FabBtn
        onClick={() => setIsOpen(true)}
        role="button"
        aria-label="Open chat"
      >
        <ChatIcon sx={{ color: "#fff", fontSize: 24 }} />
        {unreadCount > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: -3,
              right: -3,
              width: 19,
              height: 19,
              borderRadius: "50%",
              background: "#ef4444",
              border: "2px solid #fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.62rem",
              fontWeight: 700,
              color: "#fff",
            }}
          >
            {unreadCount}
          </Box>
        )}
      </FabBtn>
    );
  }

  // ── Chat Window ───────────────────────────────────────────────────────
  return (
    <ChatWindow>
      {/* Header */}
      <Header>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
          <AvatarWrap>
            <Box className="ring">
              <img src={women} alt="DragAI Support" />
            </Box>
            <OnlineDot connected={isConnected} />
          </AvatarWrap>
          <Box>
            <Typography
              sx={{
                fontFamily: "'Host Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: "0.92rem",
                color: "#fff",
                lineHeight: 1.2,
              }}
            >
              DragAI Support
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                mt: 0.3,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: isConnected ? "#4ade80" : "#fca5a5",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "rgba(255,255,255,0.78)",
                  fontFamily: "'Host Grotesk', sans-serif",
                }}
              >
                {isConnected ? "Online · Ready to help" : "Reconnecting..."}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Stack direction="row" spacing={0.3}>
          <HeaderBtn size="small" onClick={handleClose} aria-label="Minimize">
            <CollapseIcon fontSize="small" />
          </HeaderBtn>
          <HeaderBtn size="small" onClick={handleClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </HeaderBtn>
        </Stack>
      </Header>

      {/* Messages */}
      <MessagesArea>
        {messages.map((msg) => (
          <React.Fragment key={msg.id}>
            <MsgRow isbot={msg.isBot.toString()}>
              <Box>
                <Bubble isbot={msg.isBot.toString()}>{msg.text}</Bubble>
                {msg.time && (
                  <Timestamp
                    sx={{
                      px: 0.5,
                      textAlign: msg.isBot ? "left" : "right",
                    }}
                  >
                    {formatTime(msg.time)}
                  </Timestamp>
                )}
              </Box>
            </MsgRow>

            {msg.showPresetQuestions && (
              <PresetGrid>
                {presetQuestions.map((q, i) => (
                  <PresetBtn
                    key={i}
                    onClick={() => sendMessage(q)}
                    disabled={!isConnected}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        color: "#93c5fd",
                        flexShrink: 0,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </Box>
                    {q}
                  </PresetBtn>
                ))}
              </PresetGrid>
            )}
          </React.Fragment>
        ))}

        {isTyping && <TypingBubble />}
        <div ref={messagesEndRef} />
      </MessagesArea>

      {/* Input */}
      <InputArea>
        <InputRow>
          <ChatTextarea
            ref={inputRef}
            rows={1}
            placeholder="Message DragAI… (Enter to send)"
            value={inputText}
            onChange={(e) => {
              e.target.style.height = "auto";
              e.target.style.height =
                Math.min(e.target.scrollHeight, 120) + "px";
              setInputText(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            disabled={!isConnected}
            aria-label="Chat message"
          />
          <SendButton
            cansend={canSend.toString()}
            onClick={handleSend}
            disabled={!canSend}
            aria-label="Send"
            size="small"
          >
            <SendIcon sx={{ fontSize: 16 }} />
          </SendButton>
        </InputRow>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 0.7,
            px: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: "#cbd5e1",
              fontFamily: "'Host Grotesk', sans-serif",
            }}
          >
            Shift+Enter for new line
          </Typography>
          {inputText.length > MAX_CHARS * 0.7 && (
            <Typography
              sx={{
                fontSize: "0.65rem",
                color: inputText.length > MAX_CHARS ? "#ef4444" : "#94a3b8",
                fontFamily: "'Host Grotesk', sans-serif",
                transition: "color .2s",
              }}
            >
              {inputText.length}/{MAX_CHARS}
            </Typography>
          )}
        </Box>
      </InputArea>
    </ChatWindow>
  );
};

export default ChatInterface;
